import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Undo2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * BD-05 — every rule that changes which meetings the assistant asks about.
 * "Nothing is learned invisibly": each rule is listed with where it came from,
 * and Undo puts the meetings it covered back (re-classified straight away).
 *
 *   series        "Not a BD meeting" / "Delivery, not BD" on a recurring meeting
 *   title_pattern approved in Approvals after 3 consistent answers
 *   sensitivity   "skip all Personal / Confidential meetings", approved in Approvals
 */

interface Rule {
  id: number;
  rule_type: 'series' | 'title_pattern' | 'sensitivity';
  effect: 'skip' | 'delivery';
  label: string;
  created_from: 'answer' | 'approval' | 'manual';
  created_at: string;
}

function describe(rule: Rule): string {
  if (rule.rule_type === 'sensitivity') return `All ${rule.label} meetings`;
  if (rule.rule_type === 'title_pattern') return `Meetings titled like "${rule.label}"`;
  return `Recurring series "${rule.label}"`;
}

function effectLabel(rule: Rule): string {
  return rule.effect === 'delivery' ? 'Treated as delivery, not asked' : 'Skipped';
}

const BDMeetingSkipRules: React.FC = () => {
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/bd/calendar/skip-rules');
      setRules(data.rules || []);
    } catch {
      setRules([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const undo = async (rule: Rule) => {
    setBusy(rule.id);
    try {
      const { data } = await api.delete(`/api/bd/calendar/skip-rules/${rule.id}`);
      toast.success(`Undone — ${data.meetings_updated || 0} meeting(s) updated`);
      await load();
    } catch {
      toast.error('Could not undo the rule.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 px-4 py-3" data-testid="bd-skip-rules">
      <div className="text-sm font-medium text-gray-900">Meetings the assistant has learned to skip</div>
      <p className="text-xs text-gray-500 mb-2">
        From your answers on the Meetings tab and the rules you approved. Undo puts those meetings back.
      </p>
      {rules === null ? (
        <div className="flex items-center gap-2 text-xs text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
      ) : rules.length === 0 ? (
        <div className="text-xs text-gray-400" data-testid="bd-skip-rules-empty">No learned rules yet.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {rules.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-2 gap-3" data-testid={`bd-skip-rule-${r.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-gray-800 truncate">{describe(r)}</div>
                <div className="text-xs text-gray-400">
                  {effectLabel(r)} · {r.created_from === 'approval' ? 'approved in Approvals' : 'from your answer'} · {new Date(r.created_at).toLocaleDateString()}
                </div>
              </div>
              <button
                type="button"
                onClick={() => undo(r)}
                disabled={busy === r.id}
                data-testid={`bd-skip-rule-undo-${r.id}`}
                className="shrink-0 inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />} Undo
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BDMeetingSkipRules;
